import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { database, storage } from '../../firebase';
import { ref, get, set, push, remove, update } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import Swal from 'sweetalert2';
import {
  PlusIcon, TrashIcon, PencilIcon, XMarkIcon,
  FolderIcon, PhotoIcon, CubeIcon,
  ArrowsRightLeftIcon, PaintBrushIcon, ChevronDownIcon,
  ChevronRightIcon, SparklesIcon, ArrowUpTrayIcon,
  MagnifyingGlassIcon, CheckCircleIcon, Bars3Icon,
  ChevronLeftIcon, TagIcon, FolderOpenIcon, CalendarIcon, ClockIcon
} from '@heroicons/react/24/outline';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const UNIT_OPTIONS = [
  { value: 'pcs', label: 'Pieces (pcs)' }, { value: 'kg', label: 'Kilograms (kg)' },
  { value: 'g', label: 'Grams (g)' }, { value: 'l', label: 'Liters (l)' },
  { value: 'ml', label: 'Milliliters (ml)' }, { value: 'box', label: 'Box' },
  { value: 'pack', label: 'Pack' }, { value: 'bottle', label: 'Bottle' },
  { value: 'tube', label: 'Tube' }, { value: 'scoop', label: 'Scoop' },
  { value: 'pair', label: 'Pair' }, { value: 'set', label: 'Set' },
];
const CLOTHING_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL'];

const generateSKU = (name = '', brand = '', cat = '') => {
  const n = name.replace(/\s+/g, '').slice(0, 4).toUpperCase();
  const b = brand.replace(/\s+/g, '').slice(0, 3).toUpperCase();
  const c = cat.replace(/\s+/g, '').slice(0, 3).toUpperCase();
  return `${b || 'PRD'}-${n || 'ITEM'}-${c || 'GEN'}-${Math.floor(1000 + Math.random() * 9000)}`;
};

// ─── SWAL above everything ────────────────────────────────────────────────────
const SWAL_Z = { customClass: { container: 'swal-topz' } };
if (typeof document !== 'undefined' && !document.getElementById('swal-topz-style')) {
  const s = document.createElement('style');
  s.id = 'swal-topz-style';
  s.textContent = '.swal-topz{z-index:99999!important}';
  document.head.appendChild(s);
}
const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2500, toast: true, position: 'top-end', showConfirmButton: false, ...SWAL_Z });

// Helper to check if an offer is currently active
const isOfferActive = (offer) => {
  if (!offer || !offer.isActive) return false;
  const now = Date.now();
  // If no dates set, offer is active as long as isActive is true
  if (!offer.startDate && !offer.endDate) return true;
  // Check date range if both dates exist
  if (offer.startDate && offer.endDate) {
    return now >= offer.startDate && now <= offer.endDate;
  }
  // If only start date set, check if current time is after start
  if (offer.startDate && !offer.endDate) {
    return now >= offer.startDate;
  }
  // If only end date set, check if current time is before end
  if (!offer.startDate && offer.endDate) {
    return now <= offer.endDate;
  }
  return true;
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════════════════════
const ProductManagement = () => {
  const { userData, currentUser } = useAuth();

  // ─── ROLE GUARD ───────────────────────────────────────────────────────────
  const userRole = (userData?.personalInfo?.role || '').toLowerCase();
  const isAdmin = userRole === 'admin';
  const isSuperAdmin = userRole === 'superadmin';
  const isAuthorized = isAdmin || isSuperAdmin;

  const requireAuth = () => {
    if (!isAuthorized) {
      toast('error', 'Access Denied', 'Only Admins and Super-Admins can perform this action.');
      return false;
    }
    return true;
  };

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedSubSubCategory, setSelectedSubSubCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const EMPTY_OFFER = {
    isActive: false,
    price: null,
    startDate: null,
    endDate: null
  };

  const EMPTY_PRODUCT = {
    name: '', description: '', price: 0, memberPrice: 0, cost: 0,
    brand: '', sku: '', status: 'active',
    categoryId: '', subCategoryId: '', subSubCategoryId: '',
    images: [], variants: [], lowStockAlert: 5, variantType: 'simple', features: [],
    offer: { ...EMPTY_OFFER }
  };
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT);
  const [simpleQty, setSimpleQty] = useState({ quantity: 0, unit: 'pcs' });
  const [sizeOnlyRow, setSizeOnlyRow] = useState({ size: '', unit: 'pcs', quantity: 0 });
  const [colorBuilder, setColorBuilder] = useState({ color: '', sizes: [] });
  const [sizeRowForm, setSizeRowForm] = useState({ size: '', customSize: '', quantity: 0 });
  const [lowStockPerVariant, setLowStockPerVariant] = useState({});
  const [uploading, setUploading] = useState(false);

  useEffect(() => { fetchCategories(); fetchProducts(); }, []);

  const fetchCategories = async () => {
    try {
      const snap = await get(ref(database, 'categories'));
      setCategories(snap.exists() ? Object.entries(snap.val()).map(([k, v]) => ({ id: k, ...v })) : []);
    } catch (e) { console.error(e); }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const snap = await get(ref(database, 'products'));
      setProducts(snap.exists() ? Object.entries(snap.val()).map(([k, v]) => ({ id: k, ...v })) : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // ─── PRODUCT CRUD ──────────────────────────────────────────────────────────
  const openAddProduct = () => {
    if (!requireAuth()) return;
    setEditingProduct(null);
    setProductForm({ ...EMPTY_PRODUCT, offer: { ...EMPTY_OFFER } });
    setSimpleQty({ quantity: 0, unit: 'pcs' });
    setSizeOnlyRow({ size: '', unit: 'pcs', quantity: 0 });
    setColorBuilder({ color: '', sizes: [] });
    setSizeRowForm({ size: '', customSize: '', quantity: 0 });
    setLowStockPerVariant({});
    setIsProductModalOpen(true);
  };

  const openEditProduct = (p) => {
    if (!requireAuth()) return;
    setEditingProduct(p);
    setProductForm({
      name: p.name || '', description: p.description || '',
      price: p.price || 0, memberPrice: p.memberPrice || 0, cost: p.cost || 0,
      brand: p.brand || '', sku: p.sku || '', status: p.status || 'active',
      categoryId: p.categoryId || '', subCategoryId: p.subCategoryId || '',
      subSubCategoryId: p.subSubCategoryId || '',
      images: p.images || [], variants: p.variants || [],
      lowStockAlert: p.lowStockAlert || 5, variantType: p.variantType || 'simple',
      features: p.features || [],
      offer: p.offer ? { ...p.offer } : { ...EMPTY_OFFER }
    });
    
    // Load existing variant data based on type
    if (p.variantType === 'simple' && p.variants?.[0]) {
      setSimpleQty(p.variants[0]);
    }
    setLowStockPerVariant(p.lowStockPerVariant || {});
    setIsProductModalOpen(true);
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    if (!productForm.name.trim()) return toast('error', 'Product name required');
    if (productForm.price <= 0) return toast('error', 'Price must be > 0');
    
    // Validate offer price if active
    if (productForm.offer?.isActive) {
      if (!productForm.offer.price || productForm.offer.price <= 0) {
        return toast('error', 'Offer price must be greater than 0 when offer is active');
      }
      if (productForm.offer.price >= productForm.price) {
        return toast('error', 'Offer price must be less than regular price');
      }
      // Validate dates if both are set
      if (productForm.offer.startDate && productForm.offer.endDate && productForm.offer.startDate > productForm.offer.endDate) {
        return toast('error', 'Offer end date must be after start date');
      }
    }
    
    // Validate variants based on type
    if (productForm.variantType === 'simple' && (!simpleQty.quantity || simpleQty.quantity <= 0)) {
      return toast('error', 'Please set quantity for simple product');
    }
    if (productForm.variantType === 'size_only' && productForm.variants.length === 0) {
      return toast('error', 'Please add at least one size variant');
    }
    if (productForm.variantType === 'color_size' && productForm.variants.length === 0) {
      return toast('error', 'Please add at least one color variant');
    }
    
    setLoading(true);
    try {
      const catName = categories.find(c => c.id === productForm.categoryId)?.name || '';
      const sku = productForm.sku || generateSKU(productForm.name, productForm.brand, catName);
      const now = Date.now();

      // Prepare variants based on type
      let finalVariants = [];
      if (productForm.variantType === 'simple') {
        finalVariants = [{ ...simpleQty }];
      } else {
        finalVariants = productForm.variants;
      }

      const payload = {
        name:             productForm.name.trim(),
        price:            productForm.price,
        status:           productForm.status || 'active',
        updatedAt:        now,
        createdAt:        editingProduct ? (editingProduct.createdAt || now) : now,
        description:      productForm.description   || '',
        memberPrice:      productForm.memberPrice > 0 ? productForm.memberPrice : productForm.price,
        cost:             productForm.cost           || 0,
        brand:            productForm.brand          || '',
        sku,
        categoryId:       productForm.categoryId    || '',
        subCategoryId:    productForm.subCategoryId || '',
        subSubCategoryId: productForm.subSubCategoryId || '',
        images:           productForm.images        || [],
        variantType:      productForm.variantType   || 'simple',
        variants:         finalVariants,
        lowStockAlert:    productForm.lowStockAlert  ?? 5,
        createdBy:        currentUser?.uid          || '',
        createdByName:    userData?.personalInfo?.name || 'System',
        features:         productForm.features       || [],
        lowStockPerVariant: lowStockPerVariant        || {},
        offer:            productForm.offer          || { ...EMPTY_OFFER }
      };

      if (editingProduct) {
        await set(ref(database, `products/${editingProduct.id}`), { ...payload, id: editingProduct.id });
        toast('success', 'Product updated');
      } else {
        const nr = push(ref(database, 'products'));
        await set(nr, { ...payload, id: nr.key });
        toast('success', 'Product created');
      }
      setIsProductModalOpen(false);
      fetchProducts();
    } catch (err) {
      console.error('saveProduct error:', err);
      toast('error', 'Failed to save product', err?.message || '');
    }
    finally { setLoading(false); }
  };

  const deleteProduct = async (p) => {
    if (!requireAuth()) return;
    const ok = await Swal.fire({ title: `Delete "${p.name}"?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc2626', confirmButtonText: 'Delete', ...SWAL_Z });
    if (!ok.isConfirmed) return;
    await remove(ref(database, `products/${p.id}`));
    toast('success', 'Deleted'); fetchProducts();
  };

  const uploadProductImages = async (files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const ir = storageRef(storage, `products/${Date.now()}_${f.name}`);
        await uploadBytes(ir, f);
        urls.push(await getDownloadURL(ir));
      }
      setProductForm(p => ({ ...p, images: [...p.images, ...urls] }));
    } finally { setUploading(false); }
  };

  // ─── VARIANTS ─────────────────────────────────────────────────────────────
  const addSizeToColor = () => {
    const sz = sizeRowForm.size === 'custom' ? sizeRowForm.customSize : sizeRowForm.size;
    if (!sz || sizeRowForm.quantity <= 0) {
      toast('error', 'Please select size and enter quantity');
      return;
    }
    setColorBuilder(p => ({ ...p, sizes: [...p.sizes, { size: sz, quantity: sizeRowForm.quantity }] }));
    setSizeRowForm({ size: '', customSize: '', quantity: 0 });
  };
  
  const commitColor = () => {
    if (!colorBuilder.color || !colorBuilder.sizes.length) {
      toast('error', 'Please enter color name and add at least one size');
      return;
    }
    setProductForm(p => ({ ...p, variants: [...p.variants, { color: colorBuilder.color, sizes: colorBuilder.sizes }] }));
    setColorBuilder({ color: '', sizes: [] });
  };
  
  const commitSizeOnly = () => {
    if (!sizeOnlyRow.size || sizeOnlyRow.quantity <= 0) {
      toast('error', 'Please enter size/variant name and quantity');
      return;
    }
    setProductForm(p => ({ ...p, variants: [...p.variants, { size: sizeOnlyRow.size, quantity: sizeOnlyRow.quantity, unit: sizeOnlyRow.unit }] }));
    setSizeOnlyRow({ size: '', unit: 'pcs', quantity: 0 });
  };
  
  const updateSimpleQuantity = () => {
    if (simpleQty.quantity <= 0) {
      toast('error', 'Please enter a valid quantity');
      return;
    }
    // Simple product just updates the quantity state, no variants array needed yet
    // The variants array will be built during save
  };
  
  const removeVariant = (i) => setProductForm(p => ({ ...p, variants: p.variants.filter((_, idx) => idx !== i) }));
  
  const getTotalStock = (variants = [], type) => {
    if (type === 'simple') return variants[0]?.quantity || 0;
    if (type === 'size_only') return variants.reduce((s, v) => s + (v.quantity || 0), 0);
    if (type === 'color_size') return variants.reduce((s, v) => s + v.sizes.reduce((ss, sz) => ss + (sz.quantity || 0), 0), 0);
    return 0;
  };

  const addFeature = () => setProductForm(p => ({ ...p, features: [...p.features, ''] }));
  const updateFeature = (i, val) => setProductForm(p => ({ ...p, features: p.features.map((f, idx) => idx === i ? val : f) }));
  const removeFeature = (i) => setProductForm(p => ({ ...p, features: p.features.filter((_, idx) => idx !== i) }));

  // Offer helpers
  const updateOffer = (field, value) => {
    setProductForm(p => ({
      ...p,
      offer: { ...p.offer, [field]: value }
    }));
  };

  const toggleOffer = (isActive) => {
    setProductForm(p => ({
      ...p,
      offer: { ...p.offer, isActive }
    }));
  };

  const filteredProducts = products.filter(p => {
    const matchCat = selectedSubSubCategory ? p.subSubCategoryId === selectedSubSubCategory
      : selectedSubCategory ? p.subCategoryId === selectedSubCategory
      : selectedCategory ? p.categoryId === selectedCategory : true;
    const q = searchQuery.toLowerCase();
    return matchCat && (!q || p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q) || p.brand?.toLowerCase().includes(q));
  });

  const activeCatName = selectedSubSubCategory
    ? categories.find(c => c.id === selectedSubSubCategory)?.name
    : selectedSubCategory ? categories.find(c => c.id === selectedSubCategory)?.name
    : selectedCategory ? categories.find(c => c.id === selectedCategory)?.name : null;

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg,#f8f9fa 0%,#e9ecef 100%)' }}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-8">

        {/* HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold text-gray-900">Product Management</h1>
              {userRole && (
                <span className="px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wide"
                  style={{
                    background: isSuperAdmin ? '#1a1a2e' : isAdmin ? '#fef2f2' : '#f3f4f6',
                    color: isSuperAdmin ? '#fff' : isAdmin ? '#800020' : '#6b7280',
                    border: `1px solid ${isSuperAdmin ? '#1a1a2e' : isAdmin ? '#fcd5de' : '#e5e7eb'}`
                  }}>
                  {isSuperAdmin ? '⚡ Super Admin' : isAdmin ? '🛡 Admin' : '👁 Read Only'}
                </span>
              )}
            </div>
            <p className="text-gray-500 mt-1">
              {isAuthorized
                ? 'Manage your catalogue with precision'
                : 'You have read-only access — contact an Admin to make changes'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isAuthorized && (
              <>
                <button
                  onClick={() => setIsCategoryModalOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold border-2 transition-all text-sm"
                  style={{ borderColor: '#800020', color: '#800020' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <FolderIcon className="h-4 w-4" />
                  Manage Categories
                </button>
                <button onClick={openAddProduct}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all text-sm"
                  style={{ background: 'linear-gradient(135deg,#800020 0%,#b30000 100%)' }}>
                  <PlusIcon className="h-5 w-5" />
                  Add Product
                </button>
              </>
            )}
          </div>
        </div>

        {/* LAYOUT */}
        <div className="flex gap-5 items-start">

          {/* COLLAPSIBLE SIDEBAR */}
          <AnimatePresence initial={false}>
            {sidebarOpen && (
              <motion.div
                key="sidebar"
                initial={{ width: 0, opacity: 0, marginRight: -20 }}
                animate={{ width: 256, opacity: 1, marginRight: 0 }}
                exit={{ width: 0, opacity: 0, marginRight: -20 }}
                transition={{ type: 'spring', damping: 26, stiffness: 280 }}
                style={{ flexShrink: 0, overflow: 'hidden' }}>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" style={{ width: 256 }}>
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <span className="font-bold text-gray-900 text-sm tracking-wide">Categories</span>
                    <button
                      onClick={() => setSidebarOpen(false)}
                      title="Collapse sidebar"
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all">
                      <ChevronLeftIcon className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="p-2 space-y-0.5 overflow-y-auto" style={{ maxHeight: '70vh' }}>
                    <button
                      onClick={() => { setSelectedCategory(null); setSelectedSubCategory(null); setSelectedSubSubCategory(null); }}
                      className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all text-sm font-medium"
                      style={{ background: !selectedCategory ? '#800020' : 'transparent', color: !selectedCategory ? '#fff' : '#374151' }}>
                      <SparklesIcon className="h-4 w-4 flex-shrink-0" />
                      <span className="flex-1">All Products</span>
                      <span className="text-xs font-bold opacity-60">{products.length}</span>
                    </button>

                    {categories.filter(c => c.level === 0 && !c.parentId).map(cat => (
                      <CategoryNode key={cat.id}
                        cat={cat} categories={categories} products={products}
                        selectedCategory={selectedCategory}
                        selectedSubCategory={selectedSubCategory}
                        selectedSubSubCategory={selectedSubSubCategory}
                        onSelect={id => { setSelectedCategory(id); setSelectedSubCategory(null); setSelectedSubSubCategory(null); }}
                        onSelectSub={id => { setSelectedSubCategory(id); setSelectedSubSubCategory(null); }}
                        onSelectSubSub={setSelectedSubSubCategory}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* PRODUCTS AREA */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-4">
              {!sidebarOpen && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={() => setSidebarOpen(true)}
                  title="Show categories"
                  className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-gray-800 hover:border-gray-300 shadow-sm transition-all">
                  <Bars3Icon className="h-5 w-5" />
                </motion.button>
              )}
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by name, SKU or brand…"
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all shadow-sm"
                  style={{ '--tw-ring-color': '#800020' }}
                />
              </div>
              {activeCatName && (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm flex-shrink-0"
                  style={{ background: '#800020' }}>
                  <TagIcon className="h-3.5 w-3.5" />
                  {activeCatName}
                  <button onClick={() => { setSelectedCategory(null); setSelectedSubCategory(null); setSelectedSubSubCategory(null); }}
                    className="ml-1 opacity-70 hover:opacity-100">
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>

            <p className="text-xs text-gray-400 mb-4 font-medium">
              {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}{activeCatName ? ` in "${activeCatName}"` : ''}
            </p>

            {loading ? (
              <div className="text-center py-24">
                <div className="inline-block w-10 h-10 border-4 border-red-200 border-t-red-700 rounded-full animate-spin" />
                <p className="text-gray-500 mt-3 text-sm">Loading products…</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-24 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <CubeIcon className="h-14 w-14 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">No products found</p>
                <button onClick={openAddProduct} className="mt-4 text-sm font-semibold" style={{ color: '#800020' }}>
                  + Add your first product
                </button>
              </div>
            ) : (
              <div className="grid gap-3" style={{
                display: 'grid',
                gridTemplateColumns: sidebarOpen
                  ? 'repeat(auto-fill, minmax(140px, 1fr))'
                  : 'repeat(auto-fill, minmax(140px, 1fr))'
              }}>
                <AnimatePresence>
                  {filteredProducts.map(p => (
                    <ProductCard key={p.id} product={p}
                      categories={categories} getTotalStock={getTotalStock}
                      isAuthorized={isAuthorized} isOfferActive={isOfferActive}
                      onEdit={() => openEditProduct(p)} onDelete={() => deleteProduct(p)}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isProductModalOpen && (
          <ProductModal
            productForm={productForm} setProductForm={setProductForm}
            categories={categories}
            simpleQty={simpleQty} setSimpleQty={setSimpleQty}
            sizeOnlyRow={sizeOnlyRow} setSizeOnlyRow={setSizeOnlyRow}
            colorBuilder={colorBuilder} setColorBuilder={setColorBuilder}
            sizeRowForm={sizeRowForm} setSizeRowForm={setSizeRowForm}
            lowStockPerVariant={lowStockPerVariant} setLowStockPerVariant={setLowStockPerVariant}
            addSizeToColor={addSizeToColor} commitColor={commitColor}
            commitSizeOnly={commitSizeOnly} updateSimpleQuantity={updateSimpleQuantity}
            removeVariant={removeVariant}
            uploadProductImages={uploadProductImages} uploading={uploading}
            addFeature={addFeature} updateFeature={updateFeature} removeFeature={removeFeature}
            onSubmit={saveProduct} onClose={() => setIsProductModalOpen(false)}
            editing={!!editingProduct} loading={loading} generateSKU={generateSKU}
            updateOffer={updateOffer} toggleOffer={toggleOffer}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isCategoryModalOpen && (
          <CategoryManagementModal
            categories={categories} products={products}
            onClose={() => setIsCategoryModalOpen(false)}
            onRefresh={fetchCategories}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SIDEBAR — read-only filter tree
// ═══════════════════════════════════════════════════════════════════════════════
const CategoryNode = ({ cat, categories, products, selectedCategory, selectedSubCategory, selectedSubSubCategory, onSelect, onSelectSub, onSelectSubSub }) => {
  const [open, setOpen] = useState(false);
  const subs = categories.filter(c => c.parentId === cat.id && c.level === 1);
  const isActive = selectedCategory === cat.id && !selectedSubCategory;
  const count = products.filter(p => p.categoryId === cat.id).length;

  return (
    <div className="select-none">
      <div className="flex items-center rounded-xl transition-all" style={{ background: isActive ? '#800020' : 'transparent' }}>
        <button onClick={() => setOpen(o => !o)}
          className="w-7 h-9 flex items-center justify-center flex-shrink-0"
          style={{ color: isActive ? 'rgba(255,255,255,0.65)' : '#9ca3af' }}>
          {subs.length > 0
            ? (open ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />)
            : <span className="inline-block w-3.5" />}
        </button>
        <button onClick={() => { onSelect(cat.id); setOpen(true); }}
          className="flex-1 flex items-center gap-2 py-2 text-sm font-medium text-left min-w-0 transition-colors"
          style={{ color: isActive ? '#fff' : '#374151' }}>
          {cat.imageUrl
            ? <img src={cat.imageUrl} alt="" className="w-5 h-5 rounded-lg object-cover flex-shrink-0" />
            : <FolderIcon className="h-4 w-4 flex-shrink-0 opacity-50" />}
          <span className="truncate">{cat.name}</span>
        </button>
        {count > 0 && <span className="mr-2 text-xs font-bold flex-shrink-0" style={{ color: isActive ? 'rgba(255,255,255,0.65)' : '#9ca3af' }}>{count}</span>}
      </div>

      <AnimatePresence>
        {open && subs.length > 0 && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden ml-5 space-y-0.5">
            {subs.map(sub => {
              const subSubs = categories.filter(c => c.parentId === sub.id && c.level === 2);
              const subActive = selectedSubCategory === sub.id && !selectedSubSubCategory;
              return (
                <div key={sub.id}>
                  <button onClick={() => onSelectSub(sub.id)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-medium text-left transition-all"
                    style={{ background: subActive ? '#9a1a3a' : 'transparent', color: subActive ? '#fff' : '#6b7280' }}>
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: subActive ? '#fff' : '#d1d5db' }} />
                    <span className="truncate">{sub.name}</span>
                  </button>
                  {selectedSubCategory === sub.id && subSubs.length > 0 && (
                    <div className="ml-4 space-y-0.5 mt-0.5">
                      {subSubs.map(ss => (
                        <button key={ss.id} onClick={() => onSelectSubSub(ss.id)}
                          className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-xl text-xs text-left transition-all"
                          style={{ background: selectedSubSubCategory === ss.id ? '#b91c1c' : 'transparent', color: selectedSubSubCategory === ss.id ? '#fff' : '#9ca3af' }}>
                          <span className="w-1 h-1 rounded-full flex-shrink-0 bg-current opacity-40" />
                          <span className="truncate">{ss.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY MANAGEMENT MODAL — full CRUD all levels
// ═══════════════════════════════════════════════════════════════════════════════
const CategoryManagementModal = ({ categories, products, onClose, onRefresh }) => {
  const fileRef = useRef();
  const [loading, setLoading] = useState(false);
  const [expandedMain, setExpandedMain] = useState(null);
  const [expandedSub, setExpandedSub] = useState(null);
  const [form, setForm] = useState(null);

  const mainCats = categories.filter(c => c.level === 0 && !c.parentId);

  const openAdd = (level, parentId = null) =>
    setForm({ level, parentId, editId: null, name: '', image: null, imagePreview: '' });

  const openEdit = (cat) =>
    setForm({ level: cat.level, parentId: cat.parentId, editId: cat.id, name: cat.name, image: null, imagePreview: cat.imageUrl || '' });

  const closeForm = () => setForm(null);

  const handleImgFile = (file) => {
    const r = new FileReader();
    r.onload = e => setForm(f => ({ ...f, image: file, imagePreview: e.target.result }));
    r.readAsDataURL(file);
  };

  const saveForm = async () => {
    if (!form.name.trim()) return toast('error', 'Name required');
    if (form.level === 0 && !form.imagePreview) return toast('error', 'Main category requires an image');
    setLoading(true);
    try {
      let imageUrl = form.imagePreview;
      if (form.image) {
        const ir = storageRef(storage, `categories/${Date.now()}_${form.image.name}`);
        await uploadBytes(ir, form.image);
        imageUrl = await getDownloadURL(ir);
      }
      const payload = { name: form.name.trim(), imageUrl: imageUrl || '', parentId: form.parentId || null, level: form.level, updatedAt: Date.now() };
      if (form.editId) {
        await update(ref(database, `categories/${form.editId}`), payload);
        toast('success', 'Updated');
      } else {
        const nr = push(ref(database, 'categories'));
        await set(nr, { ...payload, id: nr.key, createdAt: Date.now() });
        toast('success', 'Created');
      }
      closeForm();
      await onRefresh();
    } catch (e) { console.error(e); toast('error', 'Failed'); }
    finally { setLoading(false); }
  };

  const deleteCat = async (cat) => {
    if (categories.some(c => c.parentId === cat.id)) return toast('error', 'Remove subcategories first');
    if (products.some(p => [p.categoryId, p.subCategoryId, p.subSubCategoryId].includes(cat.id))) return toast('error', 'Category has products assigned');
    const ok = await Swal.fire({ title: `Delete "${cat.name}"?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc2626', confirmButtonText: 'Delete', ...SWAL_Z });
    if (!ok.isConfirmed) return;
    await remove(ref(database, `categories/${cat.id}`));
    toast('success', 'Deleted'); await onRefresh();
  };

  const InlineForm = ({ onSave, onCancel }) => (
    <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
      className="my-2 mx-1 p-4 rounded-xl border-2 border-dashed bg-gray-50 space-y-3"
      style={{ borderColor: '#800020' }}>
      <input
        autoFocus
        value={form?.name || ''}
        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onSave(); } if (e.key === 'Escape') onCancel(); }}
        placeholder="Category name…"
        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all bg-white"
        style={{ '--tw-ring-color': '#800020' }}
      />
      {form?.level === 0 && (
        <div
          onClick={() => fileRef.current?.click()}
          className="cursor-pointer rounded-xl overflow-hidden border-2 border-dashed flex items-center justify-center transition-all"
          style={{ height: 110, borderColor: form?.imagePreview ? '#800020' : '#e5e7eb' }}>
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={e => e.target.files[0] && handleImgFile(e.target.files[0])} />
          {form?.imagePreview
            ? <img src={form.imagePreview} alt="" className="w-full h-full object-cover" />
            : <div className="text-center"><PhotoIcon className="h-6 w-6 text-gray-300 mx-auto mb-1" /><p className="text-xs text-gray-400">Click to upload image</p></div>
          }
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={onSave} disabled={loading}
          className="flex-1 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50 transition-all"
          style={{ background: '#800020' }}>
          {loading ? 'Saving…' : form?.editId ? 'Update' : 'Create'}
        </button>
        <button onClick={onCancel}
          className="px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-100 transition-all">
          Cancel
        </button>
      </div>
    </motion.div>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        className="ml-auto flex flex-col bg-white shadow-2xl"
        style={{ width: '100%', height: '100vh' }}
        onClick={e => e.stopPropagation()}>

        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4"
          style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#800020 100%)' }}>
          <div>
            <h2 className="text-xl font-bold text-white">Manage Categories</h2>
            <p className="text-white/60 text-sm mt-0.5">Add, edit and organise all category levels</p>
          </div>
          <button onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-white transition-all"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
          <div className="max-w-4xl mx-auto px-8 py-8">

          <div className="flex items-center justify-between pb-3 mb-1">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#800020' }} />
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Main Categories</span>
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-semibold">{mainCats.length}</span>
            </div>
            <button
              onClick={() => { closeForm(); openAdd(0); setExpandedMain(null); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all"
              style={{ background: '#800020' }}>
              <PlusIcon className="h-3.5 w-3.5" />
              Add Main Category
            </button>
          </div>

          {form && form.level === 0 && !form.editId && (
            <div className="mb-2">
              <InlineForm onSave={saveForm} onCancel={closeForm} />
            </div>
          )}

          <div className="space-y-2 pb-6">
            {mainCats.length === 0 && !form && (
              <div className="text-center py-12 rounded-2xl border-2 border-dashed border-gray-200">
                <FolderOpenIcon className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-400">No categories yet</p>
                <button onClick={() => openAdd(0)} className="mt-2 text-sm font-semibold" style={{ color: '#800020' }}>
                  + Create your first category
                </button>
              </div>
            )}

            {mainCats.map(cat => {
              const subs = categories.filter(c => c.parentId === cat.id && c.level === 1);
              const isExpanded = expandedMain === cat.id;
              const isEditingThis = form?.editId === cat.id && form?.level === 0;

              return (
                <div key={cat.id}
                  className="rounded-2xl border overflow-hidden transition-all"
                  style={{ borderColor: isExpanded ? '#fcd5de' : '#f1f5f9', background: isExpanded ? '#fffafc' : '#fff', boxShadow: isExpanded ? '0 2px 12px rgba(128,0,32,0.06)' : 'none' }}>

                  {isEditingThis ? (
                    <div className="p-3">
                      <InlineForm onSave={saveForm} onCancel={closeForm} />
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 px-4 py-3">
                      {cat.imageUrl
                        ? <img src={cat.imageUrl} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0 border border-gray-100" />
                        : <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0"><FolderIcon className="h-5 w-5 text-gray-400" /></div>
                      }
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{cat.name}</p>
                        <p className="text-xs text-gray-400">{subs.length} sub-categor{subs.length !== 1 ? 'ies' : 'y'}</p>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <CatBtn icon={<PlusIcon className="h-3.5 w-3.5" />} label="Add sub-category"
                          onClick={() => { closeForm(); openAdd(1, cat.id); setExpandedMain(cat.id); setExpandedSub(null); }}
                          color="#2563eb" />
                        <CatBtn icon={<PencilIcon className="h-3.5 w-3.5" />} label="Edit"
                          onClick={() => { closeForm(); openEdit(cat); setExpandedMain(cat.id); }}
                          color="#d97706" />
                        <CatBtn icon={<TrashIcon className="h-3.5 w-3.5" />} label="Delete"
                          onClick={() => deleteCat(cat)} color="#dc2626" />
                        <button
                          onClick={() => setExpandedMain(isExpanded ? null : cat.id)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all ml-1">
                          <motion.span animate={{ rotate: isExpanded ? 90 : 0 }} transition={{ duration: 0.2 }}>
                            <ChevronRightIcon className="h-4 w-4" />
                          </motion.span>
                        </button>
                      </div>
                    </div>
                  )}

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
                        className="overflow-hidden" style={{ borderTop: '1px solid #fce7ef' }}>
                        <div className="px-4 py-3 space-y-1.5" style={{ background: '#fdf5f7' }}>

                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-400" />
                              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Sub Categories</span>
                            </div>
                            <button
                              onClick={() => { closeForm(); openAdd(1, cat.id); setExpandedSub(null); }}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
                              style={{ background: '#eff6ff', color: '#2563eb' }}>
                              <PlusIcon className="h-3 w-3" />
                              Add Sub
                            </button>
                          </div>

                          {form && form.level === 1 && form.parentId === cat.id && !form.editId && (
                            <InlineForm onSave={saveForm} onCancel={closeForm} />
                          )}

                          {subs.length === 0 && !(form && form.level === 1 && form.parentId === cat.id) && (
                            <p className="text-xs text-gray-400 italic px-2 py-1">No sub-categories — click "Add Sub" above</p>
                          )}

                          {subs.map(sub => {
                            const subSubs = categories.filter(c => c.parentId === sub.id && c.level === 2);
                            const isSubExpanded = expandedSub === sub.id;
                            const isEditingSub = form?.editId === sub.id && form?.level === 1;

                            return (
                              <div key={sub.id}
                                className="rounded-xl border overflow-hidden transition-all"
                                style={{ borderColor: isSubExpanded ? '#bfdbfe' : '#e5e7eb', background: '#fff' }}>

                                {isEditingSub ? (
                                  <div className="p-2">
                                    <InlineForm onSave={saveForm} onCancel={closeForm} />
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2.5 px-3 py-2.5">
                                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#3b82f6' }} />
                                    <span className="flex-1 text-sm font-medium text-gray-700 truncate">{sub.name}</span>
                                    <span className="text-xs text-gray-400 mr-1">{subSubs.length} items</span>
                                    <div className="flex items-center gap-1">
                                      <CatBtn icon={<PlusIcon className="h-3 w-3" />} label="Add sub-sub-category"
                                        onClick={() => { closeForm(); openAdd(2, sub.id); setExpandedSub(sub.id); }}
                                        color="#7c3aed" small />
                                      <CatBtn icon={<PencilIcon className="h-3 w-3" />} label="Edit"
                                        onClick={() => { closeForm(); openEdit(sub); setExpandedSub(sub.id); }}
                                        color="#d97706" small />
                                      <CatBtn icon={<TrashIcon className="h-3 w-3" />} label="Delete"
                                        onClick={() => deleteCat(sub)} color="#dc2626" small />
                                      {subSubs.length > 0 && (
                                        <button
                                          onClick={() => setExpandedSub(isSubExpanded ? null : sub.id)}
                                          className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all ml-0.5">
                                          <motion.span animate={{ rotate: isSubExpanded ? 90 : 0 }} transition={{ duration: 0.2 }}>
                                            <ChevronRightIcon className="h-3 w-3" />
                                          </motion.span>
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                )}

                                <AnimatePresence>
                                  {isSubExpanded && (
                                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
                                      className="overflow-hidden" style={{ borderTop: '1px solid #eff6ff' }}>
                                      <div className="px-3 py-2 space-y-1" style={{ background: '#f8faff' }}>
                                        <div className="flex items-center justify-between mb-1.5">
                                          <div className="flex items-center gap-1.5">
                                            <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Sub-Sub</span>
                                          </div>
                                          <button onClick={() => { closeForm(); openAdd(2, sub.id); }}
                                            className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold"
                                            style={{ background: '#f5f3ff', color: '#7c3aed' }}>
                                            <PlusIcon className="h-3 w-3" />
                                            Add
                                          </button>
                                        </div>

                                        {form && form.level === 2 && form.parentId === sub.id && !form.editId && (
                                          <InlineForm onSave={saveForm} onCancel={closeForm} />
                                        )}

                                        {subSubs.length === 0 && !(form && form.level === 2 && form.parentId === sub.id) && (
                                          <p className="text-xs text-gray-400 italic px-2 py-1">No items yet</p>
                                        )}

                                        {subSubs.map(ss => {
                                          const isEditingSS = form?.editId === ss.id && form?.level === 2;
                                          return (
                                            <div key={ss.id} className="rounded-lg border border-gray-100 bg-white overflow-hidden">
                                              {isEditingSS ? (
                                                <div className="p-2"><InlineForm onSave={saveForm} onCancel={closeForm} /></div>
                                              ) : (
                                                <div className="flex items-center gap-2 px-3 py-2">
                                                  <span className="w-1 h-1 rounded-full flex-shrink-0 bg-purple-300" />
                                                  <span className="flex-1 text-xs font-medium text-gray-600 truncate">{ss.name}</span>
                                                  <div className="flex items-center gap-1">
                                                    <CatBtn icon={<PencilIcon className="h-2.5 w-2.5" />} label="Edit"
                                                      onClick={() => { closeForm(); openEdit(ss); }}
                                                      color="#d97706" small />
                                                    <CatBtn icon={<TrashIcon className="h-2.5 w-2.5" />} label="Delete"
                                                      onClick={() => deleteCat(ss)} color="#dc2626" small />
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
        </div>

        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-white">
          <p className="text-sm text-gray-400">{mainCats.length} main categor{mainCats.length !== 1 ? 'ies' : 'y'}</p>
          <button onClick={onClose}
            className="px-8 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg transition-all"
            style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
            Done
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const CatBtn = ({ icon, onClick, label, color, small }) => (
  <button type="button" onClick={e => { e.stopPropagation(); onClick(); }} title={label}
    className="flex items-center justify-center rounded-lg transition-all active:scale-95"
    style={{ width: small ? 24 : 28, height: small ? 24 : 28, background: color + '18', color, flexShrink: 0 }}
    onMouseEnter={e => { e.currentTarget.style.background = color + '2e'; }}
    onMouseLeave={e => { e.currentTarget.style.background = color + '18'; }}>
    {icon}
  </button>
);

// ═══════════════════════════════════════════════════════════════════════════════
// PRODUCT CARD
// ═══════════════════════════════════════════════════════════════════════════════
const ProductCard = ({ product, categories, getTotalStock, onEdit, onDelete, isAuthorized, isOfferActive }) => {
  const stock = getTotalStock(product.variants, product.variantType);
  const low = stock > 0 && stock <= (product.lowStockAlert || 5);
  const img = product.images?.[0];
  const offerActive = isOfferActive(product.offer);
  const displayPrice = offerActive && product.offer?.price ? product.offer.price : product.price;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="group bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
      style={{ width: '100%' }}>

      <div className="relative bg-gray-50" style={{ height: 90 }}>
        {img
          ? <img
              src={img}
              alt={product.name}
              className="w-full h-full"
              style={{ objectFit: 'contain', objectPosition: 'center', padding: '4px' }}
            />
          : <div className="w-full h-full flex items-center justify-center">
              <CubeIcon className="h-8 w-8 text-gray-200" />
            </div>
        }

        {(stock === 0 || low || product.status === 'hidden') && (
          <div className="absolute top-1.5 left-1.5">
            {stock === 0
              ? <span className="block w-2 h-2 rounded-full bg-red-500 ring-1 ring-white" title="Out of Stock" />
              : low
              ? <span className="block w-2 h-2 rounded-full bg-amber-400 ring-1 ring-white" title="Low Stock" />
              : <span className="block w-2 h-2 rounded-full bg-gray-400 ring-1 ring-white" title="Hidden" />
            }
          </div>
        )}
        
        {offerActive && (
          <div className="absolute top-1.5 right-1.5 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md shadow-sm">
            OFFER
          </div>
        )}

        {isAuthorized && (
          <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={onEdit}
              className="w-6 h-6 flex items-center justify-center rounded-md text-white shadow transition-all hover:scale-110"
              style={{ background: 'rgba(26,26,46,0.85)' }} title="Edit">
              <PencilIcon className="h-3 w-3" />
            </button>
            <button onClick={onDelete}
              className="w-6 h-6 flex items-center justify-center rounded-md text-white shadow transition-all hover:scale-110"
              style={{ background: 'rgba(220,38,38,0.85)' }} title="Delete">
              <TrashIcon className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>

      <div className="px-2 pt-1.5 pb-2 border-t border-gray-50">
        <p className="text-xs font-semibold text-gray-800 truncate leading-tight">{product.name}</p>
        <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
          <p className="text-xs font-bold" style={{ color: '#800020' }}>
            KES {displayPrice?.toLocaleString()}
          </p>
          {offerActive && product.offer?.price && (
            <p className="text-xs text-gray-400 line-through">
              KES {product.price?.toLocaleString()}
            </p>
          )}
        </div>
        <p className={`text-xs mt-0.5 ${stock === 0 ? 'text-red-500' : low ? 'text-amber-500' : 'text-gray-400'}`}>
          {stock === 0 ? 'Out of stock' : `${stock} in stock`}
        </p>
      </div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PRODUCT MODAL - FIXED VERSION WITH OFFER FIELD
// ═══════════════════════════════════════════════════════════════════════════════
const ProductModal = ({
  productForm, setProductForm, categories,
  simpleQty, setSimpleQty, sizeOnlyRow, setSizeOnlyRow,
  colorBuilder, setColorBuilder, sizeRowForm, setSizeRowForm,
  lowStockPerVariant, setLowStockPerVariant,
  addSizeToColor, commitColor, commitSizeOnly, updateSimpleQuantity,
  removeVariant, uploadProductImages, uploading,
  addFeature, updateFeature, removeFeature,
  onSubmit, onClose, editing, loading, generateSKU,
  updateOffer, toggleOffer
}) => {
  const fileRef = useRef();
  const mainCats = categories.filter(c => c.level === 0 && !c.parentId);
  const subCats = categories.filter(c => c.parentId === productForm.categoryId && c.level === 1);
  const subSubCats = categories.filter(c => c.parentId === productForm.subCategoryId && c.level === 2);

  const autoSKU = () => {
    const cat = categories.find(c => c.id === productForm.categoryId)?.name || '';
    setProductForm(p => ({ ...p, sku: generateSKU(p.name, p.brand, cat) }));
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        className="ml-auto flex flex-col bg-white shadow-2xl"
        style={{ width: '100%', height: '100vh' }}
        onClick={e => e.stopPropagation()}>

        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4"
          style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#800020 100%)' }}>
          <div>
            <h2 className="text-xl font-bold text-white">{editing ? 'Edit Product' : 'New Product'}</h2>
            <p className="text-white/60 text-sm mt-0.5">Fill in all required fields below</p>
          </div>
          <button onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-white transition-all"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
          <div className="max-w-5xl mx-auto px-8 py-8 space-y-8">

            <Section title="Basic Information">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <Label>Product Name *</Label>
                  <Input value={productForm.name} onChange={v => setProductForm(p => ({ ...p, name: v }))} required />
                </div>
                <div>
                  <Label>SKU</Label>
                  <div className="flex gap-2">
                    <Input value={productForm.sku} onChange={v => setProductForm(p => ({ ...p, sku: v }))} placeholder="Auto-generate or type" />
                    <button type="button" onClick={autoSKU}
                      className="px-4 py-2.5 rounded-xl text-sm font-semibold border-2 whitespace-nowrap transition-all flex-shrink-0"
                      style={{ borderColor: '#800020', color: '#800020' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      Generate
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Format: BRAND-NAME-CAT-0000</p>
                </div>
                <div>
                  <Label>Brand</Label>
                  <Input value={productForm.brand} onChange={v => setProductForm(p => ({ ...p, brand: v }))} />
                </div>
                <div className="md:col-span-2">
                  <Label>Description</Label>
                  <textarea value={productForm.description}
                    onChange={e => setProductForm(p => ({ ...p, description: e.target.value }))}
                    rows={3} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': '#800020' }} />
                </div>
              </div>
            </Section>

            <Section title="Product Features">
              <div className="space-y-2.5">
                {productForm.features.map((f, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#800020' }} />
                    <input value={f} onChange={e => updateFeature(i, e.target.value)}
                      placeholder={`Feature ${i + 1}…`}
                      className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                      style={{ '--tw-ring-color': '#800020' }} />
                    <button type="button" onClick={() => removeFeature(i)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all flex-shrink-0">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addFeature}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border-2 border-dashed w-full justify-center transition-all"
                  style={{ borderColor: '#800020', color: '#800020' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <PlusIcon className="h-4 w-4" /> Add Feature
                </button>
              </div>
            </Section>

            <Section title="Category">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><Label>Main Category</Label>
                  <Select value={productForm.categoryId} onChange={v => setProductForm(p => ({ ...p, categoryId: v, subCategoryId: '', subSubCategoryId: '' }))}>
                    <option value="">Select…</option>
                    {mainCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                </div>
                <div><Label>Sub Category</Label>
                  <Select value={productForm.subCategoryId} disabled={!productForm.categoryId} onChange={v => setProductForm(p => ({ ...p, subCategoryId: v, subSubCategoryId: '' }))}>
                    <option value="">Select…</option>
                    {subCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                </div>
                <div><Label>Sub-Sub Category</Label>
                  <Select value={productForm.subSubCategoryId} disabled={!productForm.subCategoryId} onChange={v => setProductForm(p => ({ ...p, subSubCategoryId: v }))}>
                    <option value="">Select…</option>
                    {subSubCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                </div>
              </div>
            </Section>

            <Section title="Pricing">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><Label>Regular Price (KES) *</Label><Input type="number" value={productForm.price} onChange={v => setProductForm(p => ({ ...p, price: parseFloat(v) || 0 }))} required /></div>
                <div><Label>Member Price (KES)</Label><Input type="number" value={productForm.memberPrice} onChange={v => setProductForm(p => ({ ...p, memberPrice: parseFloat(v) || 0 }))} /></div>
                <div><Label>Cost Price (KES)</Label><Input type="number" value={productForm.cost} onChange={v => setProductForm(p => ({ ...p, cost: parseFloat(v) || 0 }))} /></div>
              </div>
              {productForm.price > 0 && productForm.cost > 0 && (
                <div className="mt-3 p-3 rounded-xl bg-red-50">
                  <p className="text-sm font-medium" style={{ color: '#800020' }}>
                    Margin: KES {(productForm.price - productForm.cost).toLocaleString()} ({Math.round(((productForm.price - productForm.cost) / productForm.price) * 100)}%)
                  </p>
                </div>
              )}
            </Section>

            {/* OFFER PRICE SECTION */}
            <Section title="Offer Price">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => toggleOffer(!productForm.offer?.isActive)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${productForm.offer?.isActive ? 'bg-red-600' : 'bg-gray-200'}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${productForm.offer?.isActive ? 'translate-x-6' : 'translate-x-1'}`}
                    />
                  </button>
                  <span className="text-sm font-medium text-gray-700">
                    {productForm.offer?.isActive ? 'Offer Active' : 'Offer Inactive'}
                  </span>
                  {productForm.offer?.isActive && productForm.offer?.price && (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                      {productForm.offer.price < productForm.price ? `${Math.round(((productForm.price - productForm.offer.price) / productForm.price) * 100)}% off` : ''}
                    </span>
                  )}
                </div>

                {productForm.offer?.isActive && (
                  <div className="p-4 rounded-xl border border-red-100 bg-red-50/30 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Offer Price (KES) *</Label>
                        <Input 
                          type="number" 
                          value={productForm.offer?.price || ''} 
                          onChange={v => updateOffer('price', parseFloat(v) || 0)}
                          placeholder="e.g., 1999"
                        />
                        {productForm.offer?.price >= productForm.price && productForm.price > 0 && (
                          <p className="text-xs text-red-500 mt-1">Offer price must be less than regular price</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Start Date (Optional)</Label>
                        <div className="relative">
                          <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="datetime-local"
                            value={productForm.offer?.startDate ? new Date(productForm.offer.startDate).toISOString().slice(0, 16) : ''}
                            onChange={e => updateOffer('startDate', e.target.value ? new Date(e.target.value).getTime() : null)}
                            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                            style={{ '--tw-ring-color': '#800020' }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Leave empty for no start limit</p>
                      </div>
                      <div>
                        <Label>End Date (Optional)</Label>
                        <div className="relative">
                          <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="datetime-local"
                            value={productForm.offer?.endDate ? new Date(productForm.offer.endDate).toISOString().slice(0, 16) : ''}
                            onChange={e => updateOffer('endDate', e.target.value ? new Date(e.target.value).getTime() : null)}
                            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                            style={{ '--tw-ring-color': '#800020' }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Leave empty for no end limit</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 pt-2 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <ClockIcon className="h-4 w-4 text-gray-400" />
                        <span>Offer active now:</span>
                        <span className={`font-medium ${(productForm.offer?.startDate && Date.now() < productForm.offer.startDate) || (productForm.offer?.endDate && Date.now() > productForm.offer.endDate) ? 'text-red-500' : 'text-green-600'}`}>
                          {(productForm.offer?.startDate && Date.now() < productForm.offer.startDate) ? 'Not started yet' : 
                           (productForm.offer?.endDate && Date.now() > productForm.offer.endDate) ? 'Expired' : 'Active'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Section>

            <Section title="Product Images">
              <div className="flex flex-wrap gap-3">
                {productForm.images.map((url, i) => (
                  <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-gray-200 group">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button type="button" onClick={() => setProductForm(p => ({ ...p, images: p.images.filter((_, idx) => idx !== i) }))}
                        className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                    {i === 0 && <span className="absolute bottom-0 left-0 right-0 text-center text-white py-0.5 bg-black/50" style={{ fontSize: '0.6rem' }}>Main</span>}
                  </div>
                ))}
                <label className="w-24 h-24 border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all"
                  style={{ borderColor: uploading ? '#800020' : '#d1d5db' }}
                  onMouseEnter={e => !uploading && (e.currentTarget.style.borderColor = '#800020')}
                  onMouseLeave={e => !uploading && (e.currentTarget.style.borderColor = '#d1d5db')}>
                  <input ref={fileRef} type="file" multiple accept="image/*" className="hidden"
                    onChange={e => uploadProductImages(Array.from(e.target.files))} disabled={uploading} />
                  {uploading
                    ? <div className="w-5 h-5 border-2 border-red-700 border-t-transparent rounded-full animate-spin" />
                    : <><ArrowUpTrayIcon className="h-5 w-5 text-gray-400" /><span className="text-xs text-gray-400 mt-1">Upload</span></>
                  }
                </label>
              </div>
            </Section>

            <Section title="Inventory Management">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                {[
                  { val: 'simple', label: 'Simple', desc: 'Single quantity', Icon: CubeIcon },
                  { val: 'size_only', label: 'Sizes / Variants', desc: 'No colour (e.g. 1kg, 2kg)', Icon: ArrowsRightLeftIcon },
                  { val: 'color_size', label: 'Colour + Size', desc: 'Colour & size combos', Icon: PaintBrushIcon },
                ].map(({ val, label, desc, Icon }) => (
                  <button key={val} type="button"
                    onClick={() => {
                      setProductForm(p => ({ ...p, variantType: val, variants: [] }));
                      // Reset the variant-related states when switching types
                      if (val === 'simple') {
                        setSimpleQty({ quantity: 0, unit: 'pcs' });
                      } else if (val === 'size_only') {
                        setSizeOnlyRow({ size: '', unit: 'pcs', quantity: 0 });
                      } else if (val === 'color_size') {
                        setColorBuilder({ color: '', sizes: [] });
                        setSizeRowForm({ size: '', customSize: '', quantity: 0 });
                      }
                    }}
                    className="p-4 rounded-xl border-2 text-left transition-all"
                    style={{ borderColor: productForm.variantType === val ? '#800020' : '#e5e7eb', background: productForm.variantType === val ? '#fef2f2' : '#fff' }}>
                    <Icon className="h-5 w-5 mb-2" style={{ color: '#800020' }} />
                    <p className="text-sm font-semibold text-gray-800">{label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </button>
                ))}
              </div>

              {/* SIMPLE VARIANT - Auto-save quantity, no "Set Quantity" button */}
              {productForm.variantType === 'simple' && (
                <div className="p-5 rounded-xl border border-gray-100 bg-gray-50 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Quantity *</Label>
                      <Input 
                        type="number" 
                        value={simpleQty.quantity} 
                        onChange={v => {
                          setSimpleQty(p => ({ ...p, quantity: parseInt(v) || 0 }));
                          updateSimpleQuantity(); // Just validates, doesn't store in variants yet
                        }} 
                        required 
                      />
                    </div>
                    <div>
                      <Label>Unit</Label>
                      <Select 
                        value={simpleQty.unit} 
                        onChange={v => setSimpleQty(p => ({ ...p, unit: v }))}>
                        {UNIT_OPTIONS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Low Stock Alert (qty)</Label>
                    <Input 
                      type="number" 
                      value={productForm.lowStockAlert} 
                      onChange={v => setProductForm(p => ({ ...p, lowStockAlert: parseInt(v) || 5 }))} 
                    />
                  </div>
                  {simpleQty.quantity > 0 && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-green-700">
                        Quantity will be saved: {simpleQty.quantity} {simpleQty.unit}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* SIZE ONLY VARIANT */}
              {productForm.variantType === 'size_only' && (
                <div className="p-5 rounded-xl border border-gray-100 bg-gray-50 space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Size / Label *</Label>
                      <Input 
                        value={sizeOnlyRow.size} 
                        onChange={v => setSizeOnlyRow(p => ({ ...p, size: v }))} 
                        placeholder="e.g. 1kg, Small" 
                      />
                    </div>
                    <div>
                      <Label>Unit</Label>
                      <Select 
                        value={sizeOnlyRow.unit} 
                        onChange={v => setSizeOnlyRow(p => ({ ...p, unit: v }))}>
                        {UNIT_OPTIONS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                      </Select>
                    </div>
                    <div>
                      <Label>Quantity *</Label>
                      <Input 
                        type="number" 
                        value={sizeOnlyRow.quantity} 
                        onChange={v => setSizeOnlyRow(p => ({ ...p, quantity: parseInt(v) || 0 }))} 
                      />
                    </div>
                  </div>
                  <button 
                    type="button" 
                    onClick={commitSizeOnly} 
                    className="w-full py-2.5 rounded-xl text-sm font-semibold text-white"
                    style={{ background: '#800020' }}>
                    + Add Size Variant
                  </button>
                  
                  {productForm.variants.length > 0 && (
                    <div className="space-y-2">
                      <Label>Added Size Variants ({productForm.variants.length})</Label>
                      {productForm.variants.map((v, i) => (
                        <div key={i} className="flex items-center justify-between bg-white rounded-xl p-3 border border-gray-100">
                          <div>
                            <span className="text-sm font-medium text-gray-800">{v.size}</span>
                            <span className="text-xs text-gray-500 ml-2">{v.quantity} {v.unit}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                              <label className="text-xs text-gray-500">Low stock alert:</label>
                              <input 
                                type="number" 
                                value={lowStockPerVariant[i] ?? productForm.lowStockAlert} 
                                onChange={e => setLowStockPerVariant(p => ({ ...p, [i]: parseInt(e.target.value) || 5 }))} 
                                className="w-16 px-2 py-1 border border-gray-200 rounded-lg text-xs" 
                              />
                            </div>
                            <button 
                              type="button" 
                              onClick={() => removeVariant(i)} 
                              className="text-red-400 hover:text-red-600">
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* COLOR + SIZE VARIANT */}
              {productForm.variantType === 'color_size' && (
                <div className="p-5 rounded-xl border border-gray-100 bg-gray-50 space-y-4">
                  <div>
                    <Label>Colour Name *</Label>
                    <Input 
                      value={colorBuilder.color} 
                      onChange={v => setColorBuilder(p => ({ ...p, color: v }))} 
                      placeholder="e.g. Black, Royal Blue" 
                    />
                  </div>
                  
                  <div className="p-4 rounded-xl border border-gray-200 bg-white space-y-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Add Size to this Colour</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label>Size</Label>
                        <Select 
                          value={sizeRowForm.size} 
                          onChange={v => setSizeRowForm(p => ({ ...p, size: v }))}>
                          <option value="">Select…</option>
                          {CLOTHING_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                          <option value="custom">Custom…</option>
                        </Select>
                      </div>
                      {sizeRowForm.size === 'custom' && (
                        <div>
                          <Label>Custom Size</Label>
                          <Input 
                            value={sizeRowForm.customSize} 
                            onChange={v => setSizeRowForm(p => ({ ...p, customSize: v }))} 
                            placeholder="e.g. 3XL" 
                          />
                        </div>
                      )}
                      <div>
                        <Label>Quantity *</Label>
                        <Input 
                          type="number" 
                          value={sizeRowForm.quantity} 
                          onChange={v => setSizeRowForm(p => ({ ...p, quantity: parseInt(v) || 0 }))} 
                        />
                      </div>
                    </div>
                    <button 
                      type="button" 
                      onClick={addSizeToColor} 
                      className="w-full py-2 text-xs font-semibold rounded-lg text-white"
                      style={{ background: '#1a1a2e' }}>
                      Add Size to {colorBuilder.color || 'Colour'}
                    </button>
                    
                    {colorBuilder.sizes.length > 0 && (
                      <div className="mt-3">
                        <Label>Sizes for {colorBuilder.color}:</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {colorBuilder.sizes.map((sz, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg text-xs">
                              {sz.size}: {sz.quantity} pcs
                              <button 
                                type="button" 
                                onClick={() => setColorBuilder(p => ({ ...p, sizes: p.sizes.filter((_, idx) => idx !== i) }))}>
                                <XMarkIcon className="h-3 w-3 text-gray-400 hover:text-red-500" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <button 
                    type="button" 
                    onClick={commitColor} 
                    disabled={!colorBuilder.color || colorBuilder.sizes.length === 0}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: '#800020' }}>
                    + Add Colour Variant
                  </button>
                  
                  {productForm.variants.length > 0 && (
                    <div className="space-y-3">
                      <Label>Added Colour Variants ({productForm.variants.length})</Label>
                      {productForm.variants.map((v, i) => (
                        <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-sm text-gray-800">{v.color}</span>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1.5">
                                <label className="text-xs text-gray-500">Low stock alert:</label>
                                <input 
                                  type="number" 
                                  value={lowStockPerVariant[i] ?? productForm.lowStockAlert} 
                                  onChange={e => setLowStockPerVariant(p => ({ ...p, [i]: parseInt(e.target.value) || 5 }))} 
                                  className="w-16 px-2 py-1 border border-gray-200 rounded-lg text-xs" 
                                />
                              </div>
                              <button 
                                type="button" 
                                onClick={() => removeVariant(i)} 
                                className="text-red-400 hover:text-red-600">
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {v.sizes.map((sz, si) => (
                              <span key={si} className="px-2.5 py-1 text-xs rounded-lg font-medium" style={{ background: '#fef2f2', color: '#800020' }}>
                                {sz.size}: {sz.quantity} pcs
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Section>

            <Section title="Visibility & Status">
              <div className="flex gap-3">
                {['active', 'hidden', 'out-of-stock'].map(s => (
                  <button key={s} type="button" onClick={() => setProductForm(p => ({ ...p, status: s }))}
                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 capitalize transition-all"
                    style={{ borderColor: productForm.status === s ? '#800020' : '#e5e7eb', background: productForm.status === s ? '#800020' : '#fff', color: productForm.status === s ? '#fff' : '#6b7280' }}>
                    {s.replace('-', ' ')}
                  </button>
                ))}
              </div>
            </Section>

          </div>
        </form>

        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-white">
          <button type="button" onClick={onClose}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
            Cancel
          </button>
          <button onClick={onSubmit} disabled={loading}
            className="px-10 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg transition-all disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
            {loading ? 'Saving…' : editing ? 'Update Product' : 'Create Product'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── SHARED ───────────────────────────────────────────────────────────────────
const Label = ({ children }) => (
  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">{children}</label>
);

const Input = ({ value, onChange, type = 'text', placeholder, required, disabled }) => (
  <input 
    type={type} 
    value={value} 
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder} 
    required={required} 
    disabled={disabled}
    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all disabled:bg-gray-50 disabled:text-gray-400"
    style={{ '--tw-ring-color': '#800020' }} 
  />
);

const Select = ({ value, onChange, children, disabled }) => (
  <select 
    value={value} 
    onChange={e => onChange(e.target.value)} 
    disabled={disabled}
    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all bg-white disabled:bg-gray-50 disabled:text-gray-400">
    {children}
  </select>
);

const Section = ({ title, children }) => (
  <div>
    <div className="flex items-center gap-3 mb-4">
      <p className="text-base font-bold text-gray-900 whitespace-nowrap">{title}</p>
      <div className="flex-1 h-px bg-gray-100" />
    </div>
    {children}
  </div>
);

export default ProductManagement;